import { Alert, Group, NumberInput, Select, SimpleGrid, Stack, Table, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { ImportField, ImportMapping, ImportPreview } from "../../api/types";

export const IMPORT_FIELDS: ImportField[] = ["dev_eui", "join_eui", "app_key", "model", "supplier_id"];
const HEX_LEN: Partial<Record<ImportField, number>> = { dev_eui: 16, join_eui: 16, app_key: 32 };

/** Mirrors the backend's norm_hex for preview marking: drop 0x / separators,
 * restore leading zeros Excel strips from all-digit values. */
export function normHex(v: string, len: number): string {
  let s = v.trim().toUpperCase();
  if (s.startsWith("0X")) s = s.slice(2);
  s = s.replace(/[\s:\-_.]/g, "");
  if (/^\d+$/.test(s) && s.length < len) s = s.padStart(len, "0");
  return s;
}

export function isHex(v: string, len: number): boolean {
  return new RegExp(`^[0-9A-F]{${len}}$`).test(v);
}

const MAP_KEY = "app.importMapping";

function signature(p: ImportPreview): string | null {
  if (p.header_row < 0) return null;
  return p.columns.map((c) => c.header.trim().toLowerCase()).join("|");
}

export interface SavedMapping {
  mapping: ImportMapping;
  join_eui_default: string;
}

/** Last mapping the operator used for a file with exactly these headers. */
export function loadSavedMapping(p: ImportPreview): SavedMapping | null {
  const sig = signature(p);
  if (!sig) return null;
  try {
    const all = JSON.parse(localStorage.getItem(MAP_KEY) || "{}") as Record<string, SavedMapping>;
    const m = all[sig];
    if (!m) return null;
    // Ignore columns that no longer exist.
    const ok = IMPORT_FIELDS.every((f) => m.mapping[f] == null || m.mapping[f]! < p.columns.length);
    return ok ? m : null;
  } catch {
    return null;
  }
}

export function saveMapping(p: ImportPreview, m: SavedMapping): void {
  const sig = signature(p);
  if (!sig) return;
  try {
    const all = JSON.parse(localStorage.getItem(MAP_KEY) || "{}") as Record<string, SavedMapping>;
    all[sig] = m;
    // Keep the 20 most recent formats.
    const keys = Object.keys(all);
    for (const k of keys.slice(0, Math.max(0, keys.length - 20))) delete all[k];
    localStorage.setItem(MAP_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable */
  }
}

interface Props {
  preview: ImportPreview;
  mapping: ImportMapping;
  onMappingChange: (m: ImportMapping) => void;
  headerRow: number;
  onHeaderRowChange: (v: number) => void;
  sheet: string;
  onSheetChange: (s: string) => void;
  joinEuiDefault: string;
  onJoinEuiDefaultChange: (v: string) => void;
  appKeyDefault: string;
  onAppKeyDefaultChange: (v: string) => void;
}

export function ImportColumnMapper({
  preview,
  mapping,
  onMappingChange,
  headerRow,
  onHeaderRowChange,
  sheet,
  onSheetChange,
  joinEuiDefault,
  onJoinEuiDefaultChange,
  appKeyDefault,
  onAppKeyDefaultChange,
}: Props) {
  const { t } = useTranslation();
  const letter = (i: number) => {
    let s = "";
    let n = i + 1;
    while (n > 0) {
      const r = (n - 1) % 26;
      s = String.fromCharCode(65 + r) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  };
  const columnOptions = [
    { value: "", label: t("batchImport.notInFile") },
    ...preview.columns.map((c) => ({
      value: String(c.index),
      label: `${letter(c.index)}${c.header ? ` · ${c.header}` : ""}${
        c.samples[0] ? ` — ${c.samples[0].slice(0, 24)}` : ""
      }`,
    })),
  ];

  const setField = (f: ImportField, v: string | null) =>
    onMappingChange({ ...mapping, [f]: v ? Number(v) : null });

  const cellFor = (row: string[], f: ImportField): string => {
    const i = mapping[f];
    if (i == null) {
      if (f === "join_eui") return normHex(joinEuiDefault, 16) || "0".repeat(16);
      if (f === "app_key") return normHex(appKeyDefault, 32) || "0".repeat(32);
      return "";
    }
    const raw = row[i] ?? "";
    const len = HEX_LEN[f];
    return len ? normHex(raw, len) : raw;
  };
  const cellOk = (f: ImportField, v: string) => {
    const len = HEX_LEN[f];
    return !len || isHex(v, len);
  };

  const usedTwice = IMPORT_FIELDS.filter(
    (f) => mapping[f] != null && IMPORT_FIELDS.some((g) => g !== f && mapping[g] === mapping[f]),
  );
  const joinDefaultBad = mapping.join_eui == null && joinEuiDefault.trim() !== "" && !isHex(normHex(joinEuiDefault, 16), 16);
  const keyDefaultBad = mapping.app_key == null && appKeyDefault.trim() !== "" && !isHex(normHex(appKeyDefault, 32), 32);

  return (
    <Stack gap="sm">
      <Group align="flex-end">
        {preview.sheets.length > 1 && (
          <Select
            label={t("batchImport.sheet")}
            data={preview.sheets}
            value={sheet}
            allowDeselect={false}
            onChange={(v) => v && onSheetChange(v)}
          />
        )}
        <NumberInput
          label={t("batchImport.headerRow")}
          description={t("batchImport.headerRowHint")}
          min={0}
          max={60}
          w={170}
          value={headerRow + 1}
          onChange={(v) => {
            const n = typeof v === "number" ? v : Number(v);
            if (Number.isFinite(n) && n - 1 !== headerRow) onHeaderRowChange(Math.max(-1, n - 1));
          }}
        />
        <Text size="sm" c="dimmed">
          {t("batchImport.rowsFound", { count: preview.total_rows })}
        </Text>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        {IMPORT_FIELDS.map((f) => (
          <Select
            key={f}
            label={t(`batchImport.field.${f}`)}
            withAsterisk={f === "dev_eui"}
            data={columnOptions}
            value={mapping[f] == null ? "" : String(mapping[f])}
            allowDeselect={false}
            onChange={(v) => setField(f, v)}
            error={usedTwice.includes(f) ? t("batchImport.usedTwice") : undefined}
          />
        ))}
      </SimpleGrid>

      {mapping.join_eui == null && (
        <TextInput
          label={t("batchImport.joinEuiDefault")}
          description={
            preview.suggested_join_eui
              ? t("batchImport.joinEuiSuggested", { value: preview.suggested_join_eui })
              : t("batchImport.zerosIfEmpty")
          }
          placeholder={"0".repeat(16)}
          value={joinEuiDefault}
          onChange={(e) => onJoinEuiDefaultChange(e.currentTarget.value)}
          error={joinDefaultBad ? t("batchImport.need16") : undefined}
          ff="monospace"
        />
      )}
      {mapping.app_key == null && (
        <TextInput
          label={t("batchImport.appKeyDefault")}
          description={t("batchImport.appKeyWarn")}
          placeholder={"0".repeat(32)}
          value={appKeyDefault}
          onChange={(e) => onAppKeyDefaultChange(e.currentTarget.value)}
          error={keyDefaultBad ? t("batchImport.need32") : undefined}
          ff="monospace"
        />
      )}

      {mapping.dev_eui == null ? (
        <Alert color="red" variant="light">
          {t("batchImport.pickDevEui")}
        </Alert>
      ) : (
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            {t("batchImport.previewTitle")}
          </Text>
          <Table.ScrollContainer minWidth={600}>
            <Table striped withTableBorder fz="xs" ff="monospace">
              <Table.Thead>
                <Table.Tr>
                  {IMPORT_FIELDS.map((f) => (
                    <Table.Th key={f}>{t(`batchImport.field.${f}`)}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.rows.map((row, ri) => (
                  <Table.Tr key={ri}>
                    {IMPORT_FIELDS.map((f) => {
                      const v = cellFor(row, f);
                      const ok = cellOk(f, v);
                      return (
                        <Table.Td key={f} c={ok ? undefined : "red"} fw={ok ? undefined : 700}>
                          {v || "—"}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Text size="xs" c="dimmed">
            {t("batchImport.previewHint")}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

export function mappingValid(m: ImportMapping, joinEuiDefault: string, appKeyDefault: string): boolean {
  if (m.dev_eui == null) return false;
  const set = IMPORT_FIELDS.map((f) => m[f]).filter((v) => v != null);
  if (new Set(set).size !== set.length) return false;
  if (m.join_eui == null && joinEuiDefault.trim() && !isHex(normHex(joinEuiDefault, 16), 16)) return false;
  if (m.app_key == null && appKeyDefault.trim() && !isHex(normHex(appKeyDefault, 32), 32)) return false;
  return true;
}
